import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import { formBuilderSchema } from '@/components/FormBuilder';
import { useDocumentManagementStore } from '@/stores/documentManagement/documentManagement.store';
import {
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
  TFormBuilderEditSchemaCamel,
} from '@/types';

export const useFormBuilderTemplate = () => {
  const { formBuilderId } = useParams();
  const { state } = useLocation();

  const {
    getFormBuilderTemplate,
    setDraftFormBuilderTemplate,
    loading,
    draft,
  } = useDocumentManagementStore((state) => ({
    getFormBuilderTemplate: state.getFormBuilderTemplate,
    setDraftFormBuilderTemplate: state.setDraftFormBuilderTemplate,
    draft: state.draftFormBuilderTemplate,
    loading: state.loadingDocumentManagementPackets,
  }));

  const methods = useForm<TFormBuilderEditSchemaCamel>({
    resolver: zodResolver(formBuilderSchema),
    defaultValues: draft ?? {
      title: '',
      signatureSettings: {
        type: ESignatureSettingsType.NO_SIGNATURE,
        requiredBy:
          state?.userType === 'patient'
            ? ESignatureSettingsRequiredBy.PATIENT
            : ESignatureSettingsRequiredBy.PROVIDER,
      },
      fields: [],
    },
    mode: 'onSubmit',
  });

  const { reset } = methods;

  useEffect(() => {
    if (formBuilderId === 'empty' || !formBuilderId || !!draft) return;
    const callback = async () => {
      const value = getFormBuilderTemplate(formBuilderId);
      if (!value) return;
      const templateUserType = value.userType ?? state?.userType;
      const data = { ...value.data };
      if (
        templateUserType === 'provider' &&
        data.signatureSettings?.requiredBy !==
          ESignatureSettingsRequiredBy.PROVIDER &&
        data.signatureSettings?.type !== ESignatureSettingsType.NO_SIGNATURE
      ) {
        data.signatureSettings = {
          ...data.signatureSettings,
          requiredBy: ESignatureSettingsRequiredBy.PROVIDER,
        };
      }
      setDraftFormBuilderTemplate(data);
      reset(data);
    };
    void callback();
  }, [
    draft,
    getFormBuilderTemplate,
    formBuilderId,
    reset,
    setDraftFormBuilderTemplate,
    state?.userType,
  ]);

  useEffect(
    () => () => setDraftFormBuilderTemplate(null),
    [setDraftFormBuilderTemplate],
  );

  return {
    methods,
    loading,
  };
};
